import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { 
  Edit2, 
  Save, 
  X, 
  Image as ImageIcon, 
  Package, 
  DollarSign,
  Tag,
  CheckCircle,
  AlertCircle,
  XCircle,
  Eye,
  Camera,
  Edit
} from "lucide-react";
import { Product, useWorkstationStore } from "@/hooks/useWorkstationStore";
import { SearchableCategorySelector } from "./SearchableCategorySelector";
import { cn } from "@/lib/utils";

interface ProductStatusCardProps {
  product: Product;
  isSelected?: boolean;
  showSelection?: boolean;
  variant?: 'compact' | 'detailed';
  onEdit?: (product: Product) => void;
  onPhotoManage?: (product: Product) => void;
}

export const ProductStatusCard = ({ 
  product, 
  isSelected = false, 
  showSelection = false,
  variant = 'detailed',
  onEdit,
  onPhotoManage 
}: ProductStatusCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(product.name);
  const [editCost, setEditCost] = useState(product.costPerUnit?.toString() || '');
  
  const { 
    categories, 
    selectedProducts,
    toggleProductSelection,
    updateProduct,
    globalMultiplier 
  } = useWorkstationStore();

  const handleSave = () => {
    const updates: Partial<Product> = {};
    
    if (editName !== product.name) {
      updates.name = editName;
      updates.lastModifiedBy = 'user';
    }
    
    if (editCost !== product.costPerUnit?.toString()) {
      updates.costPerUnit = parseFloat(editCost) || undefined;
    }

    if (Object.keys(updates).length > 0) {
      if (onEdit) {
        onEdit({ ...product, ...updates });
      } else {
        updateProduct(product.id, updates);
      }
    }
    
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditName(product.name);
    setEditCost(product.costPerUnit?.toString() || '');
    setIsEditing(false);
  };

  const getStatusInfo = () => {
    const hasRequiredData = product.costPerUnit && product.categoryId;
    const hasPartialData = product.costPerUnit || product.categoryId;
    
    if (hasRequiredData) {
      return {
        status: 'ready',
        icon: CheckCircle,
        label: 'Ready',
        variant: 'default' as const,
        bgClass: 'bg-success/5 border-success/20 hover:bg-success/10'
      };
    } else if (hasPartialData) {
      return {
        status: 'needs-fix',
        icon: AlertCircle,
        label: 'Needs Fix',
        variant: 'secondary' as const,
        bgClass: 'bg-warning/5 border-warning/20 hover:bg-warning/10'
      };
    } else {
      return {
        status: 'blocked',
        icon: XCircle,
        label: 'Blocked',
        variant: 'destructive' as const,
        bgClass: 'bg-destructive/5 border-destructive/20 hover:bg-destructive/10'
      };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  const getDisplayName = () => {
    if (product.lastModifiedBy) return product.name;
    if (product.aiEnhancedName && product.aiEnhancedName !== product.name) return product.aiEnhancedName;
    if (product.masterProduct?.productName && product.masterProduct.productName !== product.name) {
      return product.masterProduct.productName;
    }
    return product.name;
  };

  const calculatedRetail = product.costPerUnit ? product.costPerUnit * globalMultiplier : 0;

  if (variant === 'compact') {
    return (
      <div 
        className={cn(
          "group flex items-center gap-4 p-4 rounded-xl border bg-card transition-all duration-200",
          "hover:shadow-md hover:scale-[1.01] hover:border-primary/20",
          showSelection && isSelected && "ring-2 ring-primary/30 bg-primary/5",
          statusInfo.bgClass
        )}
      >
        {/* Selection Checkbox */}
        {showSelection && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleProductSelection(product.id)}
            className="shrink-0"
          />
        )}

        {/* Product Image with Zoom */}
        <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted shrink-0 group">
          {product.photos && product.photos.length > 0 ? (
            <img 
              src={product.photos[0]} 
              alt={getDisplayName()}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-125 cursor-pointer"
              onClick={() => onPhotoManage?.(product)}
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <Package className="w-6 h-6 text-muted-foreground" />
            </div>
          )}
          {/* Zoom Overlay */}
          {product.photos && product.photos.length > 0 && (
            <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <Eye className="w-4 h-4 text-white" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Basic Info */}
          <div className="min-w-0">
            <h3 className="font-semibold text-sm truncate">
              {getDisplayName()}
            </h3>
            <p className="text-xs text-muted-foreground font-mono">
              {product.barcode}
            </p>
            <div className="flex items-center gap-1 mt-1">
              <Package className="w-3 h-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {product.boxes} × {product.unitsPerBox}
              </span>
            </div>
          </div>

          {/* Category & Cost */}
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground mb-1">Category</div>
            <div className="text-sm font-medium truncate">
              {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Cost: {product.costPerUnit ? `$${product.costPerUnit.toFixed(2)}` : 'No cost'}
            </div>
          </div>

          {/* Status & Actions */}
          <div className="flex items-center justify-end gap-2">
            <div className="flex flex-col items-end gap-1">
              <Badge variant={statusInfo.variant} className="text-xs">
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusInfo.label}
              </Badge>
              
              {/* SRP Display */}
              {product.costPerUnit && globalMultiplier > 1 && (
                <div className="text-xs text-muted-foreground">
                  SRP: ${(product.costPerUnit * globalMultiplier).toFixed(2)}
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onPhotoManage?.(product)}
                className="p-1 h-8 w-8"
              >
                <Camera className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onEdit?.(product)}
                className="p-1 h-8 w-8"
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={cn(
      "transition-all duration-200 hover:shadow-lg border-2 rounded-2xl",
      isSelected ? "ring-2 ring-primary/50 border-primary/30" : "",
      statusInfo.bgClass
    )}>
      <CardContent className="p-4">
        {/* Header with Status and Actions */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {showSelection && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleProductSelection(product.id)}
              />
            )}
            
            {/* Main Status Badge */}
            <Badge variant={statusInfo.variant} className="font-medium">
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusInfo.label}
            </Badge>
            
            {/* POS Status Badge */}
            <Badge 
              variant={product.posStatus === 'found' ? 'default' : 'outline'}
              className={cn(
                "text-xs",
                product.posStatus === 'found' 
                  ? 'bg-success/10 text-success border-success/20' 
                  : 'bg-warning/10 text-warning border-warning/20'
              )}
            >
              {product.posStatus === 'found' ? 'Existing in POS' : 'New Product'}
            </Badge>
          </div>
          
          <div className="flex gap-1">
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-8 w-8 p-0"
              >
                <Edit2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Product Image and Info */}
        <div className="flex gap-4 mb-4">
          {/* Product Image */}
          <div className="flex-shrink-0 w-20">
            <AspectRatio ratio={1} className="rounded-xl overflow-hidden bg-muted border">
              {product.photos && product.photos.length > 0 ? (
                <div className="relative">
                  <img
                    src={product.photos[0]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {product.photos.length > 1 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs">
                      +{product.photos.length - 1}
                    </Badge>
                  )}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
            </AspectRatio>
            
            {onPhotoManage && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPhotoManage(product)}
                className="w-full mt-2 text-xs h-6"
              >
                <Eye className="w-3 h-3 mr-1" />
                View
              </Button>
            )}
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Product name"
                  className="font-medium"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} className="h-8">
                    <Save className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} className="h-8">
                    <X className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="font-semibold text-foreground mb-1 leading-tight">{getDisplayName()}</h3>
                {getDisplayName() !== product.name && (
                  <p className="text-xs text-muted-foreground mb-1">Original: {product.name}</p>
                )}
                <p className="text-sm text-muted-foreground font-mono">{product.barcode}</p>
              </div>
            )}
          </div>
        </div>

        {/* Quantity and Pricing Grid */}
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Package className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold text-foreground">{product.boxes}</div>
            <div className="text-xs text-muted-foreground">boxes</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Package className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold text-foreground">{product.unitsPerBox}</div>
            <div className="text-xs text-muted-foreground">per box</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </div>
            {isEditing ? (
              <Input
                type="number"
                step="0.01"
                value={editCost}
                onChange={(e) => setEditCost(e.target.value)}
                placeholder="0.00"
                className="h-8 text-center text-sm"
              />
            ) : (
              <>
                <div className="text-lg font-bold text-foreground">
                  {product.costPerUnit ? `$${product.costPerUnit.toFixed(2)}` : '-'}
                </div>
                <div className="text-xs text-muted-foreground">per unit</div>
              </>
            )}
          </div>
        </div>

        {/* Category Selection */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Category</span>
          </div>
          <SearchableCategorySelector
            value={product.categoryId || ""}
            onValueChange={(value) => updateProduct(product.id, { categoryId: value || undefined })}
            categories={categories}
            placeholder="Select category"
          />
        </div>

        {/* Suggested Retail Price */}
        {product.costPerUnit && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Suggested Retail:</span>
              <span className="font-semibold text-success">
                ${calculatedRetail.toFixed(2)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              ${product.costPerUnit.toFixed(2)} × {globalMultiplier} = ${calculatedRetail.toFixed(2)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};